import React, { useState, useCallback, useEffect, useRef } from "react";
import { uploadPhotoBatch, getPhotoUrl } from "../services/photoService";
import { useNavigate } from "react-router-dom";
import { Utils } from "../utilities/Utils";
import LeafletMap from "../components/LeafletMap";
import exifr from 'exifr'
import { useDropzone } from 'react-dropzone';
import { getAddressByGeopoints } from "../services/geocodeService";
import { useAlbum } from "../contexts/AlbumContext";
import SparkMD5 from "spark-md5";

import { FiEdit } from "react-icons/fi";
import { AiOutlineDelete } from "react-icons/ai";
import { LuUpload } from "react-icons/lu";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Photo } from "../types/photo";

interface FormInput {
	title: string | null;
	description: string | null;
}

interface MetaDataInput {
	datetime: string | null;
	gpsLatitude: number | null;
	gpsLongitude: number | null;
	latDegree: number | null;
	latMinute: number | null;
	latSecond: number | null;
	latRef: string | null;
	lonDegree: number | null;
	lonMinute: number | null;
	lonSecond: number | null;
	lonRef: string | null;
	orientation: number | null;
	imgWidth: number | null;
	imgHeight: number | null;
}

interface PhotoItem {
	id: number;
	file: File | null;
	previewUrl: string;
	formInput: FormInput;
	metadata: MetaDataInput;
	address: string | null;
	addressJson: string | null;
	addressLoading: boolean;
	checksum?: string;
	error?: string;
}

const PhotoUpload = () => {
	const navigate = useNavigate();
	const { addPhoto } = useAlbum();
	const [photoItems, setPhotoItems] = useState<PhotoItem[]>([]);
	const [uploadComplete, setUploadComplete] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successMsg, setSuccessMsg] = useState<string | null>(null);
	const [fileListError, setFileListError] = useState<string | null>(null);
	const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);
	const photoItemsLengthRef = useRef(0);
	const photoItemsChecksumSet = useRef(new Set<string>());

	useEffect(() => {
		photoItemsLengthRef.current = photoItems.length;
		photoItemsChecksumSet.current.clear();
		photoItems.forEach(item => {
			if (item.checksum) {
				photoItemsChecksumSet.current.add(item.checksum);
			}
		});
		if (currentEditIndex == null && photoItems.length > 0) {
			setCurrentEditIndex(photoItems.length - 1);
		}
	}, [photoItems]);

	async function calculateChecksum(file: File): Promise<string> {
		const chunkSize = 2097152; // 2MB
		const chunks = Math.ceil(file.size / chunkSize);
		const spark = new SparkMD5.ArrayBuffer();
		const fileReader = new FileReader();

		let currentChunk = 0;

		return new Promise((resolve, reject) => {
			fileReader.onload = (e) => {
				spark.append(e.target?.result as ArrayBuffer);
				currentChunk++;

				if (currentChunk < chunks) {
					loadNext();
				} else {
					resolve(spark.end());
				}
			};

			fileReader.onerror = () => reject(fileReader.error);

			const loadNext = () => {
				const start = currentChunk * chunkSize;
				const end = Math.min(start + chunkSize, file.size);
				fileReader.readAsArrayBuffer(file.slice(start, end));
			};

			loadNext();
		});
	}

	const updatePhotoItemField = <K extends keyof PhotoItem>(
		idx: number,
		key: K,
		value: PhotoItem[K]
	) => {
		setPhotoItems(prev =>
			prev.map((item, i) => (i === idx ? { ...item, [key]: value } : item))
		);
	};

	const updateNestedField = <
		K extends keyof PhotoItem,
		F extends keyof NonNullable<PhotoItem[K]>
	>(
		idx: number,
		key: K,
		field: F,
		value: NonNullable<PhotoItem[K]>[F]
	) => {
		setPhotoItems(prev =>
			prev.map((item, i) =>
				i === idx
					? {
						...item,
						[key]: {
							...(item[key] as any),
							[field]: value
						}
					}
					: item
			)
		);
	};

	const replaceNestedObject = <K extends keyof PhotoItem>(
		idx: number,
		key: K,
		value: PhotoItem[K]
	) => {
		setPhotoItems(prev =>
			prev.map((item, i) =>
				i === idx ? { ...item, [key]: value } : item
			)
		);
	};

	const editPhoto = (idx: number) => {
		setCurrentEditIndex(idx);
	}

	const removePhoto = (idx: number) => {
		const removedPhoto = photoItems[idx];
		if (removedPhoto) {
			if (removedPhoto.checksum) {
				photoItemsChecksumSet.current.delete(removedPhoto.checksum);
			}

			setPhotoItems(prev => {
				const newItems = prev.filter((_, i) => i !== idx);
				// Update currentEditIndex
				if (newItems.length === 0) {
					setCurrentEditIndex(null);
				} else if (idx >= newItems.length) {
					setCurrentEditIndex(newItems.length - 1); // If last item was removed
				} else {
					setCurrentEditIndex(idx); // Stay at same index if possible
				}
				return newItems;
			});
		}
		setFileListError(null);
	};

	const handleUserInput = (idx: number, name: string, value: string) => {
		updateNestedField(idx, 'formInput', name as keyof FormInput, value);
	};

	const anyAddressLoading = photoItems.some(item => item.addressLoading);

	const fetchAddress = async ({ idx, lat, long }: { idx: number, lat: number, long: number }) => {
		try {
			updatePhotoItemField(idx, 'addressLoading', true);
			const data = await getAddressByGeopoints(lat, long);
			//console.log("fetchAddress", data);

			if (data) {
				updatePhotoItemField(idx, 'address', data.display_name);
				updatePhotoItemField(idx, 'addressJson', JSON.stringify(data));
			}
		} catch (error) {
			console.error("Failed to fetch address:", error);
		} finally {
			updatePhotoItemField(idx, 'addressLoading', false);
		}
	};

	const readAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as ArrayBuffer);
			reader.onerror = reject;
			reader.readAsArrayBuffer(file);
		});
	}

	const readAsDataURL = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	}

	const getImageNaturalDimensions = (file: File): Promise<{ naturalWidth: number; naturalHeight: number }> => {
		return new Promise((resolve, reject) => {
			const img = new Image();
			const reader = new FileReader();

			reader.onload = (e) => {
				img.onload = () => {
					resolve({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
				};
				img.onerror = reject;
				if (e.target && typeof e.target.result === "string") {
					img.src = e.target.result;
				} else {
					reject(new Error("Failed to read image as Data URL."));
				}
			};

			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	}

	const handleFileList = async (uploadedFiles: File[]) => {
		setError(null);
		setFileListError(null);
		setSuccessMsg(null);
		if (photoItemsLengthRef.current + uploadedFiles.length > 10) {
			setFileListError("You can only upload up to 10 files.");
			return;
		}
		let errorFiles: { type: string[]; size: string[]; duplicate: string[]; exif: string[] } = {
			"type": [],
			"size": [],
			"duplicate": [],
			"exif": []
		};
		await Promise.all(uploadedFiles.map(async (file, idx) => {
			if (!file.type.startsWith("image/jpeg")) {
				errorFiles["type"].push(file.name);
				return;
			}
			if (file.size > 10 * 1024 * 1024) {
				errorFiles["size"].push(file.name);
				return;
			}

			const arrayBuffer = await readAsArrayBuffer(file);
			const exifData = await exifr.parse(arrayBuffer, { translateValues: false });
			if (!exifData) {
				errorFiles["exif"].push(file.name);
				return;
			} else if (!exifData.GPSLatitude || !exifData.GPSLongitude) {
				errorFiles["exif"].push(file.name);
				return;
			}

			const { naturalWidth, naturalHeight } = await getImageNaturalDimensions(file);

			const checksum = await calculateChecksum(file);
			if (checksum && photoItemsChecksumSet.current.has(checksum)) {
				errorFiles["duplicate"].push(file.name);
				return;
			}
			photoItemsChecksumSet.current.add(checksum);

			const previewUrl = await readAsDataURL(file);
			const fileId = Math.floor(Math.random() * 1000000);
			exifr.parse(arrayBuffer, { translateValues: false }).then((exifData) => {
				console.log(exifData);
				const gpsLatitude = exifData.latitude ?? (exifData.GPSLatitude ? Utils.geoCoordValue(exifData.GPSLatitude[0], exifData.GPSLatitude[1], exifData.GPSLatitude[2], exifData.GPSLatitudeRef) : null);
				const gpsLongitude = exifData.longitude ?? (exifData.GPSLongitude ? Utils.geoCoordValue(exifData.GPSLongitude[0], exifData.GPSLongitude[1], exifData.GPSLongitude[2], exifData.GPSLongitudeRef) : null);
				let timestamp = null;
				if (exifData.GPSDateStamp && exifData.GPSTimeStamp) {
					let gpsTimes = exifData.GPSTimeStamp.split(":");
					timestamp = `${exifData.GPSDateStamp} ${gpsTimes[0].padStart(2, '0')}:${gpsTimes[1].padStart(2, '0')}:${gpsTimes[2].padStart(2, '0')}`;
				}

				const newPhoto: PhotoItem = {
					id: fileId,
					file: file,
					previewUrl: previewUrl,
					formInput: {
						title: null,
						description: null,
					},
					metadata: {
						datetime: timestamp,
						gpsLatitude,
						gpsLongitude,
						latDegree: exifData.GPSLatitude?.[0] ?? null,
						latMinute: exifData.GPSLatitude?.[1] ?? null,
						latSecond: exifData.GPSLatitude?.[2] ?? null,
						latRef: exifData.GPSLatitudeRef || null,
						lonDegree: exifData.GPSLongitude?.[0] ?? null,
						lonMinute: exifData.GPSLongitude?.[1] ?? null,
						lonSecond: exifData.GPSLongitude?.[2] ?? null,
						lonRef: exifData.GPSLongitudeRef || null,
						orientation: exifData.Orientation || null,
						imgWidth: naturalWidth || null,
						imgHeight: naturalHeight || null,
					},
					address: null,
					addressJson: null,
					addressLoading: false,
					checksum: checksum,
				};

				setPhotoItems((prev) => {
					const updated = [...prev, newPhoto];
					const actualIdx = updated.length - 1;
					setCurrentEditIndex(actualIdx);
					if (gpsLatitude && gpsLongitude) {
						fetchAddress({ idx: actualIdx, lat: gpsLatitude, long: gpsLongitude });
					}
					return updated;
				});
			});

		}));
		const errorMessages = {
			type: "[Only JPEG files are allowed]",
			size: "[File size exceeds 10MB]",
			duplicate: "[Duplicate file]",
			exif: "[Location information missing]"
		};
		let errMsgParts = [];
		for (const [key, files] of Object.entries(errorFiles)) {
			if (files.length > 0 && errorMessages[key as keyof typeof errorMessages]) {
				errMsgParts.push(`${errorMessages[key as keyof typeof errorMessages]} ${files.join(", ")}`);
			}
		}
		if (errMsgParts.length > 0) {
			setFileListError("⚠️Invalid file(s) detected:\n" + errMsgParts.join("\n"));
		}
	};

	const LoadingSpinner = () => (
		<AiOutlineLoading3Quarters className="animate-spin inline-block h-5 w-5 text-white" />
	);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		const uploadedFiles = acceptedFiles;
		if (!uploadedFiles) return;
		handleFileList(uploadedFiles);
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		//accept: { "image/jpeg": [".jpeg", ".jpg"] },
		//maxSize: 10 * 1024 * 1024, // 10MB
		multiple: true,
	});

	const handleUpload = async (e: any) => {
		e.preventDefault();
		if (anyAddressLoading) {
			alert("Please wait for the address to load.");
			return;
		}
		if (!photoItems || photoItems.length === 0) {
			alert("Please select a file.");
			return;
		}
		if (submitted) return;

		setSubmitted(true);
		setError(null);
		setFileListError(null);

		try {
			const formData = new FormData();
			photoItems.forEach((photo, i) => {
				const base = `photoDTOs[${i}]`;

				if (photo.file) {

					formData.append(`${base}.File`, photo.file);
					formData.append(`${base}.FileName`, photo.file.name);
					formData.append(`${base}.ContentType`, photo.file.type);
					formData.append(`${base}.FileSize`, String(photo.file.size));

					const safeAppend = (key: string, value: number | string | null | undefined) => {
						formData.append(`${base}.${key}`, value !== null && value !== undefined ? String(value) : "");
					};

					safeAppend("LatitudeDecValue", photo.metadata.gpsLatitude);
					safeAppend("LatitudeDegree", photo.metadata.latDegree);
					safeAppend("LatitudeMinutes", photo.metadata.latMinute);
					safeAppend("LatitudeSeconds", photo.metadata.latSecond);
					safeAppend("LatitudeRef", photo.metadata.latRef);

					safeAppend("LongitudeDecValue", photo.metadata.gpsLongitude);
					safeAppend("LongitudeDegree", photo.metadata.lonDegree);
					safeAppend("LongitudeMinutes", photo.metadata.lonMinute);
					safeAppend("LongitudeSeconds", photo.metadata.lonSecond);
					safeAppend("LongitudeRef", photo.metadata.lonRef);
					safeAppend("Orientation", photo.metadata.orientation);
					safeAppend("ImgWidth", photo.metadata.imgWidth);
					safeAppend("ImgHeight", photo.metadata.imgHeight);

					if (photo.metadata.datetime) {
						let datetimeStr = String(photo.metadata.datetime);
						let timestamp = Utils.metaDateToIsoString(datetimeStr);
						formData.append(`${base}.Timestamp`, timestamp);
					}

					formData.append(`${base}.Title`, photo.formInput.title || "");
					formData.append(`${base}.Description`, photo.formInput.description || "");
					formData.append(`${base}.Address`, photo.address || "");
					formData.append(`${base}.AddressJson`, photo.addressJson || "");
					formData.append(`${base}.UploadType`, "1");
				}
			});

			console.log(formData);
			let results = await uploadPhotoBatch(formData);
			let successUploadedList: number[] = [];
			let failedList: number[] = [];
			console.log(results);
			if (results) {
				interface UploadResultError {
					code: string;
					description: string;
				}
				interface UploadResult {
					success: boolean;
					errors: UploadResultError[];
					resultObj?: Photo
				}
				await Promise.all(
					(results as UploadResult[]).map(async (result: UploadResult, idx: number) => {
						if (!result.success) {
							failedList.push(idx);
							// failed, display error messages
							let msg = "";
							result.errors.forEach((error: UploadResultError) => {
								msg += error.description;
							});
							updatePhotoItemField(idx, "error", msg);
						} else {
							// success
							successUploadedList.push(idx);
							// update album context
							const uploadedPhoto = result.resultObj as Photo;
							uploadedPhoto.src = await getPhotoUrl(uploadedPhoto.photoId);
							addPhoto(uploadedPhoto);
						}
					})
				);
				if (successUploadedList.length > 0) {
					let successFileNames = successUploadedList.map(idx => {
						return photoItems[idx].file?.name;
					});
					let msg = `Successfully uploaded (${successUploadedList.length}) file(s): ${successFileNames.join(", ")}`;
					setSuccessMsg(msg);
					// Remove uploaded photos from state (in reverse order to prevent index shift)
					[...successUploadedList]
						.sort((a, b) => b - a) // sort descending
						.forEach(idx => removePhoto(idx));
				}
				if (failedList.length > 0) {
					let failedFileNames = failedList.map(idx => {
						return photoItems[idx].file?.name;
					});
					let msg = `Failed to upload (${failedList.length}) file(s): ${failedFileNames.join(", ")}`;
					setError(msg);
				}
			}
			//alert("Upload successful!");
			setUploadComplete(true);
		} catch (e: any) {
			console.log(e);
			if (e.errors) {
				const descriptions = e.errors.map((err: { code: string, description: string }) => err.description);
				setError(descriptions.join(", "));
			}
		} finally {
			setSubmitted(false);
		}
	};

	const resetForm = () => {
		setPhotoItems([]);
		setSubmitted(false);
		setUploadComplete(false);
		setError(null);
		setFileListError(null);
		photoItemsChecksumSet.current.clear();
	};

	return (
		<form className="custom-form" onSubmit={handleUpload}>
			<input {...getInputProps()} />
			{photoItems.length === 0 && (
				<div {...getRootProps()} className="flex flex-col items-center justify-center border-2 border-dashed border-accentAlt/10 rounded-xl p-10 text-center bg-secondary/50 cursor-pointer hover:border-primary hover:bg-gray-50 transition-all duration-300">
					<LuUpload className="w-12 h-12 text-primaryAlt" />

					<p className="text-lg font-semibold text-primaryAlt">
						{isDragActive ? "Drop your image here..." : "Drag & drop or click to upload"}
					</p>
					<p className="mt-4 text-sm text-primaryAlt/90">
						Accepts JPEGs only <span className="font-semibold text-primary"> — MAX 10 files, 10MB limit per file</span>.
					</p>
				</div>
			)}

			{fileListError && <p className="error-message whitespace-pre">{fileListError}</p>}

			{photoItems.length > 0 && (
				<div className="my-4">
					<h4 className="text-lg font-bold text-primaryAlt mb-4">Selected photos ({photoItems.length})</h4>
					<div className={`grid gap-4 
						grid-cols-4 
						${currentEditIndex != null && currentEditIndex != undefined ? "md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10" : "md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"}
					`}>
						{photoItems.map((photo, idx) => (
							<div
								key={photo.id}
								className={`relative group rounded-lg overflow-hidden shadow-md bg-gray-100 aspect-square ${photo.error && `border-4 border-solid border-negative`}`}
							>
								<img
									src={photo.previewUrl ?? ""}
									alt="Preview"
									className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
								/>

								{/* actions */}
								<div className={`absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100`}>
									<button
										type="button"
										title="Edit details"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											editPhoto(idx);
										}}
										className="bg-green-500 z-10 text-white p-2 rounded-full shadow-lg transform transition-transform duration-300 hover:scale-110"
									>
										<FiEdit />
									</button>
									<button
										type="button"
										title="Remove photo"
										className="bg-red-500 z-10 text-white p-2 rounded-full shadow-lg transform transition-transform duration-300 hover:scale-110"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											removePhoto(idx);
										}}
									>
										<AiOutlineDelete />
									</button>
								</div>

								{/* File name tooltip */}
								<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 text-white text-xs truncate opacity-0 group-hover:opacity-100 transition-opacity duration-300">
									{photo.file?.name}
								</div>
								{photo.error && (
									<div className="absolute top-0 left-0 group-hover:flex flex-col">
										<div className="bg-negative/70 text-white text-xs rounded px-2 py-1 shadow-lg pointer-events-none">
											⚠️{photo.error}
										</div>
									</div>
								)}
							</div>
						))}

						{/* Add more images button */}
						{photoItems.length < 10 &&
							<div {...getRootProps({
								className: "border-2 border-dashed border-gray-300 rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-gray-50 transition-all duration-300"
							})}>
								<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
									<path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
								</svg>
								<p className="text-sm text-gray-500 mt-2">Add More</p>
							</div>}
					</div>
				</div>
			)}

			{currentEditIndex !== null && currentEditIndex !== undefined && photoItems[currentEditIndex] && (
				<div className="md:flex">
					{/* Photo preview */}
					<div className="md:w-1/4 w-full flex items-center justify-center">
						{photoItems[currentEditIndex].previewUrl && <img src={photoItems[currentEditIndex].previewUrl} alt={photoItems[currentEditIndex].file?.name ?? "Preview"} />}
					</div>

					{/* Map */}
					{photoItems[currentEditIndex].metadata.gpsLatitude && photoItems[currentEditIndex].metadata.gpsLongitude && (
						<div className="md:w-2/4 w-full relative">
							<LeafletMap
								mode="upload"
								lat={photoItems[currentEditIndex].metadata.gpsLatitude ?? 0}
								lng={photoItems[currentEditIndex].metadata.gpsLongitude ?? 0}
								draggable={true}
								zoom={12}
							/>
						</div>
					)}

					{/* Metadata form */}
					<div className="md:w-1/4 w-full p-8 flex flex-col">
						{photoItems[currentEditIndex].metadata.datetime &&
							<div className="field-wrapper">
								<label htmlFor="datetime">Observation time</label>
								<input id="datetime" type="text" value={String(photoItems[currentEditIndex].metadata.datetime ?
									Utils.formatDate(new Date(Utils.metaDateToIsoString(String(photoItems[currentEditIndex].metadata.datetime))))
									: "")} disabled />
							</div>
						}

						{photoItems[currentEditIndex].address &&
							<div className="field-wrapper">
								<label htmlFor="address">Address</label>
								<textarea id="address" name="address" value={`${photoItems[currentEditIndex].address}`} disabled></textarea>
							</div>
						}

						<div className="field-wrapper">
							<label htmlFor="title">Title (Optional)</label>
							<input id="title" name="title" type="text" value={String(photoItems[currentEditIndex].formInput.title ?? "")}
								onChange={(e) => handleUserInput(currentEditIndex, e.target.name, e.target.value)} />
						</div>

						<div className="field-wrapper">
							<label htmlFor="description">Description (Optional)</label>
							<textarea id="description" name="description"
								onChange={(e) => handleUserInput(currentEditIndex, e.target.name, e.target.value)}
								value={String(photoItems[currentEditIndex].formInput.description ?? "")}>
							</textarea>
						</div>
					</div>
				</div>
			)}
			{error && <p className="error-message">{error}</p>}
			{successMsg && <p className="success-message">{successMsg}</p>}
			{/* Buttons */}
			{photoItems.length > 0 &&
				<div className="flex flex-col space-y-2 my-2">
					<button type="submit" className="custom-btn" disabled={submitted}>
						{submitted ?
							<span><LoadingSpinner /> Processing...</span> :
							<span><LuUpload className="inline-block h-5 w-5" /> Confirm & Submit All</span>}
					</button>
				</div>
			}

		</form>
	);
};

export default PhotoUpload;